// Per-owner access status as the project map shows it. The formal access title (OwnerAccess)
// moves only when GK accepts a consent („Zatwierdź i przenieś"), so between the negotiator's
// declaration and that click the title still reads „Nie rozpoczęto" while the WPW order
// already says „Pozyskano zgodę pisemną". The map's „Dostęp" cell therefore leads with the
// status of the owner's current WPW whenever the title has not settled access on its own.

import {
  OWNER_ACCESS_STATUS_COLOR,
  OWNER_ACCESS_STATUS_LABEL,
  WPW_ACQUISITION_STATUSES,
  WPW_AWAITING_FILE_STATUS,
  WPW_DECLARED_STATUS_COLOR,
  WPW_DECLARED_STATUS_LABEL,
  type OwnerAccess,
  type WpwHandover,
} from "./types";

// Orders that never ran — same notion of a live WPW order as the backend's
// „pakiet nieaktualny" (wpw_supersede.py). Finished / closed orders still count.
const DEAD_ORDER_STATUSES = new Set(["cancelled", "rejected"]);

// Among WPW nobody has declared anything on yet: the newest live order first, a WPW
// detached from its order (wniosek o zmianę) next, one superseded by a newer live order last.
function rank(h: WpwHandover): number {
  if (h.superseded_by) return 0;
  if (!h.order) return 1;
  return 2;
}

// The latest news from the field wins: a consent declared in an older order is not hidden
// by a newer order the negotiator has not touched yet (the cell then says the package is
// outdated). Only when no WPW has a declaration does the order itself decide.
function isBetter(h: WpwHandover, prev: WpwHandover): boolean {
  const started = h.declared_status !== "not_started";
  if (started !== (prev.declared_status !== "not_started")) return started;
  if (started) return (h.declared_status_at ?? "") > (prev.declared_status_at ?? "");
  return rank(h) > rank(prev);
}

/** owner-id → the WPW that speaks for that owner now. Cancelled / rejected orders never
 *  count; among the rest the latest declaration wins (see `isBetter`). */
export function currentWpwByOwner(handovers: readonly WpwHandover[]): Map<string, WpwHandover> {
  const m = new Map<string, WpwHandover>();
  for (const h of handovers) {
    if (h.order && DEAD_ORDER_STATUSES.has(h.order.status)) continue;
    const prev = m.get(h.owner.id);
    if (!prev || isBetter(h, prev)) m.set(h.owner.id, h);
  }
  return m;
}

export interface OwnerAccessLike {
  access: Pick<OwnerAccess, "status" | "status_display" | "is_stale"> | null;
  in_open_case?: boolean;
  in_open_office_case?: boolean;
}

export interface OwnerAccessCell {
  color: string;
  label: string;
  /** The main line is the WPW status from the order, not the formal title. */
  fromWpw: boolean;
  /** Formal title is SECURED on an outdated route version. */
  stale: boolean;
  /** Where the WPW claim stands with GK (acquisition / collected statuses only). */
  gkNote: { text: string; color: string } | null;
  /** The formal title, worth naming under a WPW-led line (anything but „Nie rozpoczęto"). */
  titleNote: string | null;
  /** The WPW shown comes from an order a newer live WPW order replaced. */
  supersededNote: string | null;
  /** True for an acquisition GK has not accepted or rejected yet. */
  awaitingGk: boolean;
  handover: WpwHandover | null;
}

// A title that decides access by itself — the WPW below it is already history.
function titleSettles(access: OwnerAccessLike["access"]): boolean {
  if (!access) return false;
  return (access.status === "secured" && !access.is_stale) || access.status === "not_required";
}

// The title as the „Działki" tab reads it (ownerAccessMark): „Nie rozpoczęto" under an open
// sprawa is „W trakcie", „Problem" under an open office sprawa is „W trakcie" too.
function titleView(o: OwnerAccessLike): { color: string; label: string } {
  const a = o.access;
  if (a?.status === "problem" && o.in_open_office_case)
    return { color: OWNER_ACCESS_STATUS_COLOR.in_progress, label: "W trakcie (wniosek w urzędzie)" };
  if ((!a || a.status === "not_started") && o.in_open_case)
    return { color: OWNER_ACCESS_STATUS_COLOR.in_progress, label: "W trakcie (otwarta sprawa)" };
  const status = a?.status ?? "not_started";
  return {
    color: OWNER_ACCESS_STATUS_COLOR[status] ?? OWNER_ACCESS_STATUS_COLOR.not_started,
    label: a?.status_display || OWNER_ACCESS_STATUS_LABEL[status],
  };
}

// An acquisition claim GK has not decided yet: no decision on the latest claim, or a
// revoked approval (the backend's „cofnij zatwierdzenie" puts it back to awaiting GK).
function isAwaitingGk(h: WpwHandover): boolean {
  if (!WPW_ACQUISITION_STATUSES.includes(h.declared_status)) return false;
  const decision = h.current_confirmation?.decision;
  return !decision || decision === "revoked";
}

function gkNote(h: WpwHandover): { text: string; color: string } | null {
  if (h.declared_status === WPW_AWAITING_FILE_STATUS)
    return { text: "czeka na skan zgody", color: "#0284c7" };
  if (!WPW_ACQUISITION_STATUSES.includes(h.declared_status)) return null;
  if (isAwaitingGk(h)) return { text: "czeka na zatwierdzenie GK", color: "#d97706" };
  switch (h.current_confirmation?.decision) {
    case "rejected":
      return { text: "odrzucona przez GK", color: "#dc2626" };
    case "needs_info":
      return { text: "GK: wymaga uzupełnienia", color: "#d97706" };
    default:
      return { text: "zatwierdzona przez GK", color: "#059669" };
  }
}

/** What the „Dostęp" cell shows for one owner, given the owner's current WPW (if any). */
export function ownerAccessCell(o: OwnerAccessLike, h: WpwHandover | undefined): OwnerAccessCell {
  const stale = !!o.access?.is_stale && o.access.status === "secured";
  const title = titleView(o);
  if (h && h.declared_status !== "not_started" && !titleSettles(o.access)) {
    const note = gkNote(h);
    const titleStatus = o.access?.status ?? "not_started";
    return {
      color: WPW_DECLARED_STATUS_COLOR[h.declared_status],
      label: WPW_DECLARED_STATUS_LABEL[h.declared_status],
      fromWpw: true,
      stale: false,
      gkNote: note,
      titleNote:
        titleStatus === "not_started" ? null : `tytuł: ${title.label}${stale ? " (nieakt. wersja)" : ""}`,
      supersededNote: h.superseded_by
        ? `pakiet nieaktualny — nowsze zlecenie ${h.superseded_by.order_number ?? "WPW"}`
        : null,
      awaitingGk: isAwaitingGk(h),
      handover: h,
    };
  }
  return {
    ...title,
    fromWpw: false,
    stale,
    gkNote: null,
    titleNote: null,
    supersededNote: null,
    awaitingGk: false,
    handover: h ?? null,
  };
}
